import { TranslateService } from '@ngx-translate/core';

import { Injectable, OnDestroy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import {
  Observable,
  Subject,
  distinctUntilChanged,
  filter,
  map,
  of,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import {
  UserProfileViewModel,
  AuthModuleConfigId,
  MwAuthService,
  MwUserService,
} from '@core/auth';

import { BaseForm } from 'app/@shared/@models/base.form';
import { UserLoginViewModel, UserProfileUpdateDto } from './@models';
import { UserProfileApiService } from './user-profile-api.service';

@Injectable()
export class UserProfileForm extends BaseForm<
  UserProfileViewModel,
  UserProfileUpdateDto
> {
  userName: FormControl<string> | null = null;
  firstName: FormControl<string> | null = null;
  lastName: FormControl<string> | null = null;
  userLanguage: FormControl<string> | null = null;
  workPhoneNumber: FormControl<string> | null = null;
  linkedAccount: string = '';
  linkedAccountProviderKey: string = '';
  accessTokenUserName: string = '';

  get isDevincoConnectActive(): boolean {
    return this._isDevincoConnectActive;
  }

  get allowSave(): boolean {
    return this._allowSave;
  }

  get detectUIChanges(): Observable<void> {
    return this._detectUIChanges.asObservable();
  }

  get linkWithConfigId(): AuthModuleConfigId {
    return this._linkWithConfigId;
  }

  get isLinkWithMobileWorker(): boolean {
    return this.linkWithConfigId === AuthModuleConfigId.MobileWorker;
  }

  get linkWithUserName(): string {
    return this._linkWithUserName;
  }

  get selectedCompanyId(): number {
    return this._selectedCompanyId;
  }

  private _linkWithConfigId?: AuthModuleConfigId;
  private _linkWithUserName?: string;
  private _isDevincoConnectActive = false;
  private _selectedCompanyId?: number;
  private _allowSave = false;
  private _detectUIChanges = new Subject<void>();

  constructor(
    private readonly authService: MwAuthService,
    private readonly userProfileApiService: UserProfileApiService,
    private readonly userService: MwUserService
  ) {
    super({
      controlNames: [
        'userName',
        'firstName',
        'lastName',
        'userLanguage',
        'workPhoneNumber',
      ],
      idSelector: (data) => data.userId,
      errorHandlers: [],
      validators: {
        firstName: [Validators.required],
        lastName: [Validators.required],
      },
      asyncValidators: {},
      titleBuilder: {
        fields: ['firstName', 'lastName'],
      },
    });

    this.onReady = () => {};
    this.setupData();
  }

  refreshUserLoginsData(): Observable<UserLoginViewModel[]> {
    return this.userProfileApiService.getLogins().pipe(
      tap((logins) => {
        const devincoLogin = logins.filter(
          (t) => t.loginProvider === 'Devinco'
        )[0];
        this.linkedAccount = devincoLogin?.providerUserName ?? '';
        this.linkedAccountProviderKey = devincoLogin?.providerKey;
      })
    );
  }

  private setupData(): void {
    this._isDevincoConnectActive = this.authService.isDevincoConnectActive();
    this._linkWithConfigId = this.isDevincoConnectActive
      ? AuthModuleConfigId.MobileWorker
      : AuthModuleConfigId.DevincoConnect;

    this.authService
      .checkAuthMultiple()
      .pipe(
        filter((responses) => responses.length > 0),
        map((responses) => {
          const response = responses.filter(
            (t) => t.configId === this.linkWithConfigId
          )[0];
          const userData = response?.userData;

          this._linkWithUserName = userData
            ? this.linkWithConfigId === AuthModuleConfigId.DevincoConnect
              ? userData.username
              : userData.name
            : null;

          this._selectedCompanyId =
            (userData && userData['mw-company-id']) || null;
          return responses.some((t) => t.isAuthenticated);
        }),
        switchMap((hasMwAccessToken) =>
          hasMwAccessToken ? this.refreshUserLoginsData() : of(true)
        ),
        switchMap(() => this.userService.userProfile$),
        filter((userProfile) => !!userProfile),
        take(1)
      )
      .subscribe((userProfileData) => {
        this.setData({
          ...userProfileData,
          userLanguage:
            userProfileData.userLanguage?.toLocaleLowerCase() ?? 'en',
        });
      });

    this.id$
      .pipe(skip(1), takeUntil(this._unsubscribeAll$), distinctUntilChanged())
      .subscribe((id) => {
        this._allowSave = id > 0;
        this.triggerDetectUIChanges();
      });
  }

  private triggerDetectUIChanges(): void {
    this._detectUIChanges.next();
  }
}
